import { assertApplicationResponsibilityCoverage } from "@/features/chat/services/application-responsibility";
import type { PromptAppPlan } from "@/features/chat/services/app-spec-from-prompt";
import { validateActionFlowTopologyContract } from "@/features/chat/services/actionflow-topology";
import { readStudioProjectPlan } from "@/features/project/lib/studio-project-plan";
import {
  fingerprintAppGenerationSpec,
  type AppSpecFull,
} from "@/services/block-api-service";

export type StudioProvidedApplicationAuthority =
  | {
      mode: "provided-spec";
      appSpec: AppSpecFull;
      appSpecRef: string;
      evidenceRefs: string[];
      lockedPaths: string[];
      openSlots: [];
    }
  | {
      mode: "partial-provided-spec";
      appSpec: Partial<AppSpecFull>;
      appSpecRef: string;
      evidenceRefs: string[];
      lockedPaths: string[];
      openSlots: string[];
    }
  | {
      mode: "locked-exact-spec";
      appSpec: AppSpecFull;
      appSpecRef: string;
      evidenceRefs: string[];
      lockedPaths: string[];
      openSlots: string[];
    };

const DANGEROUS_POINTER_SEGMENTS = new Set([
  "__proto__",
  "prototype",
  "constructor",
]);

const APP_SPEC_FULL_ALLOWED_KEYS = new Set<string>([
  "appId",
  "projectId",
  "name",
  "goldenSkuId",
  "tier",
  "appClass",
  "description",
  "targetPlatform",
  "generationTargetPlatform",
  "runtimeTargets",
  "projectPlan",
  "designBoundApplicationArtifact",
  "libraryRagOrchestrationEvidence",
  "applicationCompositionPlan",
  "adaptiveRuntime",
  "planningSource",
  "decomposePlanning",
  "generationEvidence",
  "generationResponsibilities",
  "generationQuality",
  "templateEnforcement",
  "templateGenerationModelPolicy",
  "ontologyRetrievalTrace",
  "imageEvidenceVerificationEnabled",
  "imageSourceEvidence",
  "imageEvidenceSource",
  "pixelRagVisualSegments",
  "generatedUiVerificationSurface",
  "generatedUiVerificationSurfaces",
  "actionFlowTopology",
  "actionFlowValidationProof",
  "actionFlowBindingPlan",
  "actionFlowCompiledFlows",
  "actionFlowCompilerProof",
  "actionFlowGenerationAuthority",
  "actionFlowRuntimeSimulationProof",
  "actionFlowReadiness",
  "applicationKind",
  "targetAudience",
  "isValidationHarness",
  "reportApplicationSchemaVersion",
  "reportApplicationKind",
  "reportApplicationIntegration",
  "reportExportReadiness",
  "reportReadiness",
  "documentExportReadiness",
  "deterministic",
  "pages",
  "blocks",
  "datasources",
  "globalState",
  "interactionContract",
  "themeHint",
  "designSystemTokens",
  "designSystemContract",
  "designSystem",
  "goldenRecipePack",
  "designContract",
]);

const APP_SPEC_BLOCK_ALLOWED_KEYS = new Set([
  "name",
  "type",
  "description",
  "binds",
  "specV3",
]);
const APP_SPEC_PAGE_ALLOWED_KEYS = new Set([
  "id",
  "route",
  "title",
  "blocks",
]);
const APP_SPEC_DATASOURCE_ALLOWED_KEYS = new Set([
  "name",
  "type",
  "schema",
]);
const APP_SPEC_THEME_HINT_ALLOWED_KEYS = new Set([
  "primaryColor",
  "style",
  "fontFamily",
]);
const APP_SPEC_INTERACTION_CONTRACT_ALLOWED_KEYS = new Set([
  "actionFlowRequired",
  "requiredEventTypes",
  "requiredStateScopes",
  "requireResultsTrace",
  "requireComputedProjection",
  "notes",
]);
const APP_SPEC_GLOBAL_STATE_DECLARATION_ALLOWED_KEYS = new Set([
  "type",
  "default",
]);
const APP_SPEC_STRING_MAP_KEYS = new Set([
  "designSystemTokens",
  "designSystem",
  "designContract",
]);
const APP_SPEC_OPEN_JSON_KEYS = new Set([
  "reportApplicationIntegration",
  "reportExportReadiness",
  "reportReadiness",
  "documentExportReadiness",
  "designSystemContract",
]);
const APP_SPEC_ATOMIC_KEYS = new Set(
  [...APP_SPEC_FULL_ALLOWED_KEYS].filter(
    (key) =>
      ![
        "pages",
        "blocks",
        "datasources",
        "globalState",
        "interactionContract",
        "themeHint",
        ...APP_SPEC_STRING_MAP_KEYS,
        ...APP_SPEC_OPEN_JSON_KEYS,
      ].includes(key),
  ),
);

function rejectUnknownKeys(
  value: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  label: string,
): void {
  const unknown = Object.keys(value).filter((key) => !allowed.has(key));
  if (unknown.length > 0) {
    throw new Error(
      `AppSpec structure is invalid: ${label} contains unknown closed-schema fields ${unknown.join(", ")}.`,
    );
  }
}

/**
 * Conservative resource limits for the user-authored JSON Pointer surface.
 * They bound parser work before any duplicate or overlap scans run.
 */
export const APPLICATION_AUTHORITY_POINTER_LIMITS = Object.freeze({
  maxLockedPaths: 64,
  maxOpenSlots: 32,
  maxPointerLength: 256,
  maxSegments: 8,
  maxSegmentLength: 64,
  maxArrayIndex: 1024,
});

/** @internal Shared with the exact-authority adapter to prevent parser drift. */
export interface BoundedApplicationPointer {
  canonical: string;
  segments: string[];
}

/** Locale-independent UTF-16/code-unit ordering for canonical set-like data. */
export function compareApplicationAuthorityStrings(
  left: string,
  right: string,
): number {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/** @internal Shared canonical RFC 6901 encoding. */
export function encodeApplicationPointerSegment(segment: string): string {
  return segment.replaceAll("~", "~0").replaceAll("/", "~1");
}

function parsePointer(pointer: string): BoundedApplicationPointer {
  if (typeof pointer !== "string") {
    throw new Error("Application authority JSON Pointer must be a string.");
  }
  if (pointer.length > APPLICATION_AUTHORITY_POINTER_LIMITS.maxPointerLength) {
    throw new Error(
      `Application authority JSON Pointer length exceeds ${APPLICATION_AUTHORITY_POINTER_LIMITS.maxPointerLength} code units.`,
    );
  }
  if (!pointer.startsWith("/") || pointer === "/") {
    throw new Error(
      `Application authority path must be an absolute non-root JSON Pointer: ${pointer}`,
    );
  }
  const rawSegments = pointer.slice(1).split("/");
  if (rawSegments.length > APPLICATION_AUTHORITY_POINTER_LIMITS.maxSegments) {
    throw new Error(
      `Application authority JSON Pointer segment count exceeds ${APPLICATION_AUTHORITY_POINTER_LIMITS.maxSegments}.`,
    );
  }
  if (rawSegments.some((segment) => segment.length === 0)) {
    throw new Error(
      `Application authority path contains an empty pointer segment: ${pointer}`,
    );
  }
  const segments = rawSegments.map((rawSegment) => {
    if (
      rawSegment.length > APPLICATION_AUTHORITY_POINTER_LIMITS.maxSegmentLength
    ) {
      throw new Error(
        `Application authority JSON Pointer segment length exceeds ${APPLICATION_AUTHORITY_POINTER_LIMITS.maxSegmentLength} code units.`,
      );
    }
    if (/~(?:[^01]|$)/.test(rawSegment)) {
      throw new Error(
        `Application authority path contains a malformed JSON Pointer escape: ${pointer}`,
      );
    }
    const segment = rawSegment.replaceAll("~1", "/").replaceAll("~0", "~");
    if (DANGEROUS_POINTER_SEGMENTS.has(segment)) {
      throw new Error(
        `Application authority path contains a forbidden pointer segment: ${pointer}`,
      );
    }
    return segment;
  });
  if (!APP_SPEC_FULL_ALLOWED_KEYS.has(segments[0])) {
    throw new Error(
      `Application authority locked path or open-slot declared path targets an unknown supplied AppSpec field: ${pointer}`,
    );
  }
  assertCanonicalApplicationAuthorityPointer(segments, pointer);
  return {
    canonical: `/${segments.map(encodeApplicationPointerSegment).join("/")}`,
    segments,
  };
}

function assertCanonicalArrayIndexSegment(segment: string, pointer: string): void {
  if (!/^(?:0|[1-9]\d*)$/.test(segment)) {
    throw new Error(
      `Application authority path contains a noncanonical array index: ${pointer}`,
    );
  }
}

function rejectUnknownNestedPointer(pointer: string): never {
  throw new Error(
    `Application authority path is outside the canonical nested AppSpec schema: ${pointer}`,
  );
}

function assertCanonicalApplicationAuthorityPointer(
  segments: readonly string[],
  pointer: string,
): void {
  if (segments.length === 1) return;
  const [root, second, third, fourth] = segments;
  if (APP_SPEC_OPEN_JSON_KEYS.has(root)) rejectUnknownNestedPointer(pointer);
  if (APP_SPEC_ATOMIC_KEYS.has(root)) rejectUnknownNestedPointer(pointer);

  if (root === "pages") {
    assertCanonicalArrayIndexSegment(second, pointer);
    if (segments.length === 2) return;
    if (!APP_SPEC_PAGE_ALLOWED_KEYS.has(third)) rejectUnknownNestedPointer(pointer);
    if (segments.length === 3) return;
    if (third !== "blocks" || segments.length !== 4) rejectUnknownNestedPointer(pointer);
    assertCanonicalArrayIndexSegment(fourth, pointer);
    return;
  }
  if (root === "blocks") {
    assertCanonicalArrayIndexSegment(second, pointer);
    if (segments.length === 2) return;
    if (!APP_SPEC_BLOCK_ALLOWED_KEYS.has(third)) rejectUnknownNestedPointer(pointer);
    if (segments.length === 3) return;
    if (third !== "binds" || segments.length !== 4) rejectUnknownNestedPointer(pointer);
    assertCanonicalArrayIndexSegment(fourth, pointer);
    return;
  }
  if (root === "datasources") {
    assertCanonicalArrayIndexSegment(second, pointer);
    if (segments.length === 2) return;
    if (!APP_SPEC_DATASOURCE_ALLOWED_KEYS.has(third)) rejectUnknownNestedPointer(pointer);
    if (segments.length === 3) return;
    if (third !== "schema" || segments.length !== 4) rejectUnknownNestedPointer(pointer);
    return;
  }
  if (root === "globalState") {
    if (segments.length === 2) return;
    if (
      segments.length !== 3 ||
      !APP_SPEC_GLOBAL_STATE_DECLARATION_ALLOWED_KEYS.has(third)
    ) {
      rejectUnknownNestedPointer(pointer);
    }
    return;
  }
  if (root === "interactionContract") {
    if (!APP_SPEC_INTERACTION_CONTRACT_ALLOWED_KEYS.has(second)) {
      rejectUnknownNestedPointer(pointer);
    }
    if (segments.length === 2) return;
    if (
      segments.length !== 3 ||
      !["requiredEventTypes", "requiredStateScopes", "notes"].includes(second)
    ) {
      rejectUnknownNestedPointer(pointer);
    }
    assertCanonicalArrayIndexSegment(third, pointer);
    return;
  }
  if (root === "themeHint") {
    if (segments.length !== 2 || !APP_SPEC_THEME_HINT_ALLOWED_KEYS.has(second)) {
      rejectUnknownNestedPointer(pointer);
    }
    return;
  }
  if (APP_SPEC_STRING_MAP_KEYS.has(root)) {
    if (segments.length !== 2) rejectUnknownNestedPointer(pointer);
    return;
  }
  rejectUnknownNestedPointer(pointer);
}

/** @internal Parse, bound, and deduplicate pointers before quadratic scans. */
export function parseUniqueApplicationAuthorityPointers(
  pointers: readonly string[],
  label: string,
  maximum: number,
): BoundedApplicationPointer[] {
  if (pointers.length > maximum) {
    throw new Error(
      `Application authority contains too many ${label} paths; limit is ${maximum}.`,
    );
  }
  const parsed = pointers.map(parsePointer);
  const canonical = new Set<string>();
  for (const pointer of parsed) {
    if (canonical.has(pointer.canonical)) {
      throw new Error(
        `Application authority contains a duplicate normalized ${label} pointer: ${pointer.canonical}`,
      );
    }
    canonical.add(pointer.canonical);
  }
  return parsed;
}

function isAncestorOrEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length <= right.length &&
    left.every((segment, index) => segment === right[index])
  );
}

function rejectOverlappingPointers(
  pointers: readonly BoundedApplicationPointer[],
  label: string,
): void {
  for (let left = 0; left < pointers.length; left += 1) {
    for (let right = left + 1; right < pointers.length; right += 1) {
      if (
        isAncestorOrEqual(pointers[left].segments, pointers[right].segments) ||
        isAncestorOrEqual(pointers[right].segments, pointers[left].segments)
      ) {
        throw new Error(
          `Application authority contains overlapping ${label} paths.`,
        );
      }
    }
  }
}

function rejectLockedOpenOverlap(
  lockedPaths: readonly BoundedApplicationPointer[],
  openSlots: readonly BoundedApplicationPointer[],
): void {
  for (const locked of lockedPaths) {
    for (const open of openSlots) {
      if (
        isAncestorOrEqual(locked.segments, open.segments) ||
        isAncestorOrEqual(open.segments, locked.segments)
      ) {
        throw new Error(
          "Application authority locked paths cannot overlap open slots.",
        );
      }
    }
  }
}

function arrayPointerIndex(
  array: readonly unknown[],
  segment: string,
  pointer: BoundedApplicationPointer,
  label: string,
  requireExisting: boolean,
): number {
  if (!/^(?:0|[1-9]\d*)$/.test(segment)) {
    throw new Error(
      `${label} contains a noncanonical array index at ${pointer.canonical}.`,
    );
  }
  const index = Number(segment);
  if (
    !Number.isSafeInteger(index) ||
    index > APPLICATION_AUTHORITY_POINTER_LIMITS.maxArrayIndex
  ) {
    throw new Error(
      `${label} array index exceeds ${APPLICATION_AUTHORITY_POINTER_LIMITS.maxArrayIndex} at ${pointer.canonical}.`,
    );
  }
  if (
    requireExisting &&
    (index >= array.length ||
      !Object.prototype.hasOwnProperty.call(array, index))
  ) {
    throw new Error(
      `${label} does not contain declared path ${pointer.canonical}.`,
    );
  }
  return index;
}

function readPointerSegment(
  current: object,
  segment: string,
  pointer: BoundedApplicationPointer,
  label: string,
): unknown {
  if (Array.isArray(current)) {
    const index = arrayPointerIndex(current, segment, pointer, label, true);
    return current[index];
  }
  if (!Object.prototype.hasOwnProperty.call(current, segment)) {
    throw new Error(
      `${label} does not contain declared path ${pointer.canonical}.`,
    );
  }
  return (current as Record<string, unknown>)[segment];
}

/** @internal Resolve only own JSON properties with strict JSON array semantics. */
export function readApplicationAuthorityPointer(
  root: unknown,
  pointer: BoundedApplicationPointer,
  label: string,
): unknown {
  let current = root;
  for (const segment of pointer.segments) {
    if (current === null || typeof current !== "object") {
      throw new Error(
        `${label} does not contain declared path ${pointer.canonical}.`,
      );
    }
    current = readPointerSegment(current, segment, pointer, label);
  }
  if (current === undefined) {
    throw new Error(
      `${label} does not contain a JSON value at declared path ${pointer.canonical}.`,
    );
  }
  return current;
}

function writeOwnPointer(
  root: Record<string, unknown>,
  pointer: BoundedApplicationPointer,
  value: unknown,
  candidate: unknown,
): void {
  let current: Record<string, unknown> | unknown[] = root;
  let candidateCurrent = candidate;
  for (let index = 0; index < pointer.segments.length - 1; index += 1) {
    const segment = pointer.segments[index];
    if (candidateCurrent === null || typeof candidateCurrent !== "object") {
      throw new Error(
        `Planner candidate does not contain declared path ${pointer.canonical}.`,
      );
    }
    candidateCurrent = readPointerSegment(
      candidateCurrent,
      segment,
      pointer,
      "Planner candidate",
    );
    const destinationKey = Array.isArray(current)
      ? arrayPointerIndex(
          current,
          segment,
          pointer,
          "Human application authority",
          false,
        )
      : segment;
    const existing = current[destinationKey as keyof typeof current];
    if (existing === null || typeof existing !== "object") {
      const replacement: Record<string, unknown> | unknown[] = Array.isArray(
        candidateCurrent,
      )
        ? []
        : {};
      (current as Record<string, unknown>)[destinationKey] = replacement;
      current = replacement;
    } else {
      if (Array.isArray(existing) !== Array.isArray(candidateCurrent)) {
        throw new Error(
          `Application authority container shape conflicts at ${pointer.canonical}.`,
        );
      }
      current = existing as Record<string, unknown> | unknown[];
    }
  }
  const finalSegment = pointer.segments.at(-1)!;
  const finalKey = Array.isArray(current)
    ? arrayPointerIndex(
        current,
        finalSegment,
        pointer,
        "Human application authority",
        false,
      )
    : finalSegment;
  (current as Record<string, unknown>)[finalKey] = structuredClone(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isStringMap(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

function hasFullApplicationSpecRequiredFields(
  value: Record<string, unknown>,
): value is AppSpecFull & Record<string, unknown> {
  return (
    isNonEmptyString(value.name) &&
    Array.isArray(value.blocks) &&
    value.blocks.every(
      (block) =>
        isRecord(block) &&
        isNonEmptyString(block.name) &&
        isNonEmptyString(block.type) &&
        typeof block.description === "string",
    )
  );
}

function rejectAppSpecStructure(detail: string): never {
  throw new Error(`AppSpec structure is invalid: ${detail}.`);
}

const APP_SPEC_PROJECT_PLAN_ALLOWED_KEYS = new Set([
  "intent",
  "outputKind",
  "appClass",
  "runtimeTargets",
  "designSystemId",
  "stylePresetId",
  "designSystemRelease",
  "designOntologyBinding",
  "templateId",
  "dynamicIslands",
  "securityProfile",
  "performanceBudget",
  "adaptiveRuntime",
]);
const GOLDEN_RECIPE_PACK_ALLOWED_KEYS = new Set([
  "schemaVersion",
  "recipeIds",
  "designSystemGates",
  "recipes",
]);
const GOLDEN_RECIPE_ALLOWED_KEYS = new Set([
  "id",
  "scenarioId",
  "label",
  "requiredBlocks",
  "designQualityGates",
  "actionFlowContracts",
  "stateContracts",
  "datasourceContracts",
  "browserProof",
]);
const GOLDEN_RECIPE_IDS = new Set([
  "shopping",
  "complex-mobile",
  "backoffice-data-table",
]);

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function sameCanonicalJson(left: unknown, right: unknown): boolean {
  return (
    fingerprintAppGenerationSpec({
      name: "Nested AppSpec comparison",
      blocks: [],
      reportApplicationIntegration: { value: left },
    }) ===
    fingerprintAppGenerationSpec({
      name: "Nested AppSpec comparison",
      blocks: [],
      reportApplicationIntegration: { value: right },
    })
  );
}

function assertCanonicalProjectPlan(value: unknown): void {
  if (!isRecord(value)) rejectAppSpecStructure("projectPlan must be an object");
  rejectUnknownKeys(value, APP_SPEC_PROJECT_PLAN_ALLOWED_KEYS, "projectPlan");
  const normalized = readStudioProjectPlan({ projectPlan: value });
  if (
    !normalized ||
    normalized.intent !== value.intent ||
    normalized.appClass !== value.appClass ||
    !sameCanonicalJson(normalized.runtimeTargets, value.runtimeTargets)
  ) {
    rejectAppSpecStructure("projectPlan does not match the canonical project plan contract");
  }
  for (const key of APP_SPEC_PROJECT_PLAN_ALLOWED_KEYS) {
    if (
      key === "intent" ||
      key === "appClass" ||
      key === "runtimeTargets" ||
      value[key] === undefined
    ) {
      continue;
    }
    if (!sameCanonicalJson(normalized[key as keyof typeof normalized], value[key])) {
      rejectAppSpecStructure(
        `projectPlan.${key} does not match the canonical project plan contract`,
      );
    }
  }
}

function assertCanonicalGoldenRecipePack(value: unknown): void {
  if (!isRecord(value)) {
    rejectAppSpecStructure("goldenRecipePack must be an object");
  }
  rejectUnknownKeys(value, GOLDEN_RECIPE_PACK_ALLOWED_KEYS, "goldenRecipePack");
  const gates = value.designSystemGates;
  if (
    value.schemaVersion !== "studio-golden-recipe-pack.v1" ||
    !Array.isArray(value.recipeIds) ||
    !value.recipeIds.every((id) => GOLDEN_RECIPE_IDS.has(String(id))) ||
    !isRecord(gates) ||
    gates.spacingScalePx !== 8 ||
    gates.minTouchTargetPx !== 44 ||
    gates.requireSemanticTokens !== true ||
    gates.forbidAdjacentControlsWithoutGap !== true ||
    gates.requireMeasuredDesignEvidence !== true ||
    !Array.isArray(value.recipes)
  ) {
    rejectAppSpecStructure("goldenRecipePack does not match its canonical contract");
  }
  rejectUnknownKeys(
    gates,
    new Set([
      "spacingScalePx",
      "minTouchTargetPx",
      "requireSemanticTokens",
      "forbidAdjacentControlsWithoutGap",
      "requireMeasuredDesignEvidence",
    ]),
    "goldenRecipePack.designSystemGates",
  );
  for (const [index, recipe] of value.recipes.entries()) {
    if (!isRecord(recipe)) {
      rejectAppSpecStructure(`goldenRecipePack.recipes[${index}] must be an object`);
    }
    rejectUnknownKeys(
      recipe,
      GOLDEN_RECIPE_ALLOWED_KEYS,
      `goldenRecipePack.recipes[${index}]`,
    );
    if (
      !isNonEmptyString(recipe.id) ||
      !GOLDEN_RECIPE_IDS.has(String(recipe.scenarioId)) ||
      !isNonEmptyString(recipe.label) ||
      ![
        recipe.requiredBlocks,
        recipe.designQualityGates,
        recipe.actionFlowContracts,
        recipe.stateContracts,
        recipe.datasourceContracts,
        recipe.browserProof,
      ].every(isStringArray)
    ) {
      rejectAppSpecStructure(
        `goldenRecipePack.recipes[${index}] does not match its canonical contract`,
      );
    }
  }
}

function assertClosedEvidenceRecord(
  value: unknown,
  allowedKeys: ReadonlySet<string>,
  label: string,
): asserts value is Record<string, unknown> {
  if (!isRecord(value)) rejectAppSpecStructure(`${label} must be an object`);
  rejectUnknownKeys(value, allowedKeys, label);
}

function assertIsoTimestamp(value: unknown, label: string): void {
  if (!isNonEmptyString(value) || !Number.isFinite(Date.parse(value))) {
    rejectAppSpecStructure(`${label} must be an ISO timestamp`);
  }
}

function assertFiniteNumber(value: unknown, label: string): void {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    rejectAppSpecStructure(`${label} must be a finite number`);
  }
}

function assertStringArrayField(value: unknown, label: string): void {
  if (!isStringArray(value)) {
    rejectAppSpecStructure(`${label} must be a string array`);
  }
}

function assertEvidenceIssue(
  value: unknown,
  label: string,
  optionalIds: readonly string[] = [],
): void {
  assertClosedEvidenceRecord(
    value,
    new Set(["code", "severity", "message", ...optionalIds]),
    label,
  );
  if (
    !isNonEmptyString(value.code) ||
    !["error", "warning"].includes(String(value.severity)) ||
    !isNonEmptyString(value.message)
  ) {
    rejectAppSpecStructure(`${label} does not match the canonical issue contract`);
  }
  for (const id of optionalIds) {
    if (value[id] !== undefined && !isNonEmptyString(value[id])) {
      rejectAppSpecStructure(`${label}.${id} must be a nonempty string`);
    }
  }
}

const ACTIONFLOW_PRIMITIVE_KINDS = new Set([
  "set-local-state",
  "promote-local-to-global",
  "hydrate-local-from-global",
  "fetch-datasource-to-global",
  "write-global-to-datasource",
  "compute-projection",
  "navigate",
  "guard-by-permission",
  "trigger-ui-feedback",
  "subscribe-realtime-source",
]);

function assertActionFlowTopology(value: unknown, appSpec: AppSpecFull): void {
  assertClosedEvidenceRecord(
    value,
    new Set(["schemaVersion", "source", "generatedAt", "nodes", "relations", "primitives", "effectBoundaries"]),
    "actionFlowTopology",
  );
  if (
    value.schemaVersion !== "studio-actionflow-state-topology.v1" ||
    !isNonEmptyString(value.source) ||
    !Array.isArray(value.nodes) ||
    !Array.isArray(value.relations) ||
    !Array.isArray(value.primitives) ||
    !Array.isArray(value.effectBoundaries)
  ) {
    rejectAppSpecStructure("actionFlowTopology does not match its canonical contract");
  }
  assertIsoTimestamp(value.generatedAt, "actionFlowTopology.generatedAt");
  for (const [index, node] of value.nodes.entries()) {
    assertClosedEvidenceRecord(
      node,
      new Set(["id", "kind", "scope", "path", "writable", "ontologyRefs", "evidenceRefs", "ownerBlockName", "datasourceName"]),
      `actionFlowTopology.nodes[${index}]`,
    );
    if (
      !isNonEmptyString(node.id) ||
      !isNonEmptyString(node.kind) ||
      !isNonEmptyString(node.scope) ||
      !isNonEmptyString(node.path) ||
      typeof node.writable !== "boolean"
    ) rejectAppSpecStructure(`actionFlowTopology.nodes[${index}] is malformed`);
    assertStringArrayField(node.ontologyRefs, `actionFlowTopology.nodes[${index}].ontologyRefs`);
    assertStringArrayField(node.evidenceRefs, `actionFlowTopology.nodes[${index}].evidenceRefs`);
  }
  for (const [index, relation] of value.relations.entries()) {
    assertClosedEvidenceRecord(
      relation,
      new Set(["id", "kind", "sourceNodeId", "targetNodeId", "primitiveRef", "effectBoundaryRef", "evidenceRefs"]),
      `actionFlowTopology.relations[${index}]`,
    );
    if (![relation.id, relation.kind, relation.sourceNodeId, relation.targetNodeId, relation.primitiveRef].every(isNonEmptyString)) {
      rejectAppSpecStructure(`actionFlowTopology.relations[${index}] is malformed`);
    }
    assertStringArrayField(relation.evidenceRefs, `actionFlowTopology.relations[${index}].evidenceRefs`);
  }
  for (const [index, primitive] of value.primitives.entries()) {
    assertClosedEvidenceRecord(
      primitive,
      new Set(["id", "kind", "label", "required", "inputNodeIds", "outputNodeIds", "effectBoundaryRefs", "compilerTargetAction", "compilerTargetStepType", "compilerParamBindings", "ontologyRefs", "evidenceRefs"]),
      `actionFlowTopology.primitives[${index}]`,
    );
    if (
      !isNonEmptyString(primitive.id) ||
      !ACTIONFLOW_PRIMITIVE_KINDS.has(String(primitive.kind)) ||
      !isNonEmptyString(primitive.label) ||
      typeof primitive.required !== "boolean" ||
      !Array.isArray(primitive.compilerParamBindings)
    ) rejectAppSpecStructure(`actionFlowTopology.primitives[${index}] is malformed`);
    for (const field of ["inputNodeIds", "outputNodeIds", "effectBoundaryRefs", "ontologyRefs", "evidenceRefs"]) {
      assertStringArrayField(primitive[field], `actionFlowTopology.primitives[${index}].${field}`);
    }
    for (const [bindingIndex, binding] of primitive.compilerParamBindings.entries()) {
      assertClosedEvidenceRecord(
        binding,
        new Set(["role", "param", "sourceNodeId", "targetNodeId", "expression", "resultKey", "value", "evidenceRefs"]),
        `actionFlowTopology.primitives[${index}].compilerParamBindings[${bindingIndex}]`,
      );
      if (!isNonEmptyString(binding.role) || !isNonEmptyString(binding.param)) {
        rejectAppSpecStructure(`actionFlowTopology primitive binding is malformed`);
      }
      assertStringArrayField(binding.evidenceRefs, `actionFlowTopology primitive binding evidenceRefs`);
    }
  }
  for (const [index, boundary] of value.effectBoundaries.entries()) {
    assertClosedEvidenceRecord(
      boundary,
      new Set(["id", "kind", "sideEffect", "evidenceRefs", "requiredCapability"]),
      `actionFlowTopology.effectBoundaries[${index}]`,
    );
    if (!isNonEmptyString(boundary.id) || !isNonEmptyString(boundary.kind) || typeof boundary.sideEffect !== "boolean") {
      rejectAppSpecStructure(`actionFlowTopology.effectBoundaries[${index}] is malformed`);
    }
    assertStringArrayField(boundary.evidenceRefs, `actionFlowTopology.effectBoundaries[${index}].evidenceRefs`);
  }
  let proof;
  try {
    proof = validateActionFlowTopologyContract(value as never, appSpec);
  } catch {
    rejectAppSpecStructure("actionFlowTopology cannot be validated by the canonical topology validator");
  }
  if (!proof.valid) rejectAppSpecStructure("actionFlowTopology fails the canonical topology validator");
}

function assertActionFlowValidationProof(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "valid", "issues", "summary"]), "actionFlowValidationProof");
  const summary = value.summary;
  if (value.schemaVersion !== "studio-actionflow-validation-proof.v1" || typeof value.valid !== "boolean" || !Array.isArray(value.issues)) {
    rejectAppSpecStructure("actionFlowValidationProof does not match its canonical contract");
  }
  value.issues.forEach((issue, index) => assertEvidenceIssue(issue, `actionFlowValidationProof.issues[${index}]`, ["nodeId", "primitiveId", "relationId"]));
  assertClosedEvidenceRecord(summary, new Set(["nodes", "relations", "primitives", "effectBoundaries", "requiredPrimitiveKinds", "coveredPrimitiveKinds", "missingPrimitiveKinds"]), "actionFlowValidationProof.summary");
  for (const field of ["nodes", "relations", "primitives", "effectBoundaries"]) assertFiniteNumber(summary[field], `actionFlowValidationProof.summary.${field}`);
  for (const field of ["requiredPrimitiveKinds", "coveredPrimitiveKinds", "missingPrimitiveKinds"]) {
    assertStringArrayField(summary[field], `actionFlowValidationProof.summary.${field}`);
    if (!(summary[field] as string[]).every((kind) => ACTIONFLOW_PRIMITIVE_KINDS.has(kind))) rejectAppSpecStructure(`actionFlowValidationProof.summary.${field} contains an unknown primitive kind`);
  }
}

function assertActionFlowBindingPlan(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "source", "generatedAt", "bindings"]), "actionFlowBindingPlan");
  if (value.schemaVersion !== "studio-actionflow-binding-plan.v1" || !isNonEmptyString(value.source) || !Array.isArray(value.bindings)) {
    rejectAppSpecStructure("actionFlowBindingPlan does not match its canonical contract");
  }
  assertIsoTimestamp(value.generatedAt, "actionFlowBindingPlan.generatedAt");
  for (const [index, binding] of value.bindings.entries()) {
    assertClosedEvidenceRecord(binding, new Set(["id", "primitiveRef", "primitiveKind", "flowName", "trigger", "event", "inputNodeIds", "outputNodeIds", "effectBoundaryRefs", "expectedResultKey", "compilerTargetAction", "compilerTargetStepType", "evidenceRefs"]), `actionFlowBindingPlan.bindings[${index}]`);
    if (
      ![binding.id, binding.primitiveRef, binding.flowName].every(isNonEmptyString) ||
      !ACTIONFLOW_PRIMITIVE_KINDS.has(String(binding.primitiveKind)) ||
      !["event", "init", "manual"].includes(String(binding.trigger))
    ) rejectAppSpecStructure(`actionFlowBindingPlan.bindings[${index}] is malformed`);
    for (const field of ["inputNodeIds", "outputNodeIds", "effectBoundaryRefs", "evidenceRefs"]) assertStringArrayField(binding[field], `actionFlowBindingPlan.bindings[${index}].${field}`);
    assertClosedEvidenceRecord(binding.event, new Set(["blockName", "eventType", "source", "evidenceRefs"]), `actionFlowBindingPlan.bindings[${index}].event`);
    if (!isNonEmptyString(binding.event.blockName) || !isNonEmptyString(binding.event.eventType) || !["component-event", "lifecycle-init", "manual"].includes(String(binding.event.source))) rejectAppSpecStructure(`actionFlowBindingPlan.bindings[${index}].event is malformed`);
    assertStringArrayField(binding.event.evidenceRefs, `actionFlowBindingPlan.bindings[${index}].event.evidenceRefs`);
  }
}

function assertActionFlowCompilerProof(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "valid", "issues", "summary"]), "actionFlowCompilerProof");
  if (value.schemaVersion !== "studio-actionflow-compiler-proof.v1" || typeof value.valid !== "boolean" || !Array.isArray(value.issues)) rejectAppSpecStructure("actionFlowCompilerProof does not match its canonical contract");
  value.issues.forEach((issue, index) => assertEvidenceIssue(issue, `actionFlowCompilerProof.issues[${index}]`, ["bindingId", "primitiveId", "flowName"]));
  assertClosedEvidenceRecord(value.summary, new Set(["requiredPrimitives", "boundPrimitives", "compiledFlows", "missingPrimitiveRefs"]), "actionFlowCompilerProof.summary");
  for (const field of ["requiredPrimitives", "boundPrimitives", "compiledFlows"]) assertFiniteNumber(value.summary[field], `actionFlowCompilerProof.summary.${field}`);
  assertStringArrayField(value.summary.missingPrimitiveRefs, "actionFlowCompilerProof.summary.missingPrimitiveRefs");
}

function assertActionFlowGenerationAuthority(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "registrySchemaVersion", "generatedAt", "targetPlatform", "targetRuntime", "valid", "referencedActionNames", "admittedActionNames", "adapterRequiredActionNames", "directAtomicActionNames", "issues", "evidenceRefs"]), "actionFlowGenerationAuthority");
  if (
    value.schemaVersion !== "studio-actionflow-generation-authority.v1" ||
    !isNonEmptyString(value.registrySchemaVersion) ||
    !["react", "flutter"].includes(String(value.targetPlatform)) ||
    !["web", "flutter"].includes(String(value.targetRuntime)) ||
    typeof value.valid !== "boolean" ||
    !Array.isArray(value.issues)
  ) rejectAppSpecStructure("actionFlowGenerationAuthority does not match its canonical contract");
  assertIsoTimestamp(value.generatedAt, "actionFlowGenerationAuthority.generatedAt");
  for (const field of ["referencedActionNames", "admittedActionNames", "adapterRequiredActionNames", "directAtomicActionNames", "evidenceRefs"]) assertStringArrayField(value[field], `actionFlowGenerationAuthority.${field}`);
  for (const [index, issue] of value.issues.entries()) {
    assertClosedEvidenceRecord(issue, new Set(["actionName", "reason", "flowNames"]), `actionFlowGenerationAuthority.issues[${index}]`);
    if (
      !isNonEmptyString(issue.actionName) ||
      ![
        "capability-missing",
        "inactive",
        "not-public",
        "runtime-missing",
        "generation-forbidden",
        "target-unsupported",
      ].includes(String(issue.reason))
    ) rejectAppSpecStructure(`actionFlowGenerationAuthority.issues[${index}] is malformed`);
    assertStringArrayField(issue.flowNames, `actionFlowGenerationAuthority.issues[${index}].flowNames`);
  }
}

function assertActionFlowRuntimeProof(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "source", "generatedAt", "valid", "issues", "stateMutations", "mockEvidence", "lifecycleEvidence", "summary"]), "actionFlowRuntimeSimulationProof");
  if (value.schemaVersion !== "studio-actionflow-runtime-simulation-proof.v1" || !isNonEmptyString(value.source) || typeof value.valid !== "boolean" || !Array.isArray(value.issues) || !Array.isArray(value.stateMutations) || !Array.isArray(value.mockEvidence)) rejectAppSpecStructure("actionFlowRuntimeSimulationProof does not match its canonical contract");
  assertIsoTimestamp(value.generatedAt, "actionFlowRuntimeSimulationProof.generatedAt");
  value.issues.forEach((issue, index) => assertEvidenceIssue(issue, `actionFlowRuntimeSimulationProof.issues[${index}]`, ["bindingId", "flowName", "stepName"]));
  for (const [index, mutation] of value.stateMutations.entries()) {
    assertClosedEvidenceRecord(mutation, new Set(["bindingId", "flowName", "scope", "path", "sourceAction", "evidenceRefs"]), `actionFlowRuntimeSimulationProof.stateMutations[${index}]`);
    if (
      ![mutation.bindingId, mutation.flowName, mutation.path, mutation.sourceAction].every(isNonEmptyString) ||
      !["local", "appData", "computed", "_results", "route"].includes(String(mutation.scope))
    ) rejectAppSpecStructure(`actionFlowRuntimeSimulationProof.stateMutations[${index}] is malformed`);
    assertStringArrayField(mutation.evidenceRefs, `actionFlowRuntimeSimulationProof.stateMutations[${index}].evidenceRefs`);
  }
  for (const [index, evidence] of value.mockEvidence.entries()) {
    assertClosedEvidenceRecord(evidence, new Set(["bindingId", "flowName", "kind", "target", "resultRef", "startedAtMs", "completedAtMs", "durationMs", "evidenceRefs"]), `actionFlowRuntimeSimulationProof.mockEvidence[${index}]`);
    if (
      ![evidence.bindingId, evidence.flowName, evidence.target].every(isNonEmptyString) ||
      !["datasource", "navigation", "auth", "ui", "realtime", "motion"].includes(String(evidence.kind))
    ) rejectAppSpecStructure(`actionFlowRuntimeSimulationProof.mockEvidence[${index}] is malformed`);
    for (const field of ["startedAtMs", "completedAtMs", "durationMs"] as const) {
      if (evidence[field] !== undefined) assertNonNegativeFiniteNumber(evidence[field], `actionFlowRuntimeSimulationProof.mockEvidence[${index}].${field}`);
    }
    if (
      typeof evidence.startedAtMs === "number" &&
      typeof evidence.completedAtMs === "number" &&
      evidence.completedAtMs < evidence.startedAtMs
    ) rejectAppSpecStructure(`actionFlowRuntimeSimulationProof.mockEvidence[${index}] has incoherent timing`);
    if (
      typeof evidence.startedAtMs === "number" &&
      typeof evidence.completedAtMs === "number" &&
      typeof evidence.durationMs === "number" &&
      evidence.durationMs !== evidence.completedAtMs - evidence.startedAtMs
    ) rejectAppSpecStructure(`actionFlowRuntimeSimulationProof.mockEvidence[${index}] has incoherent duration`);
    assertStringArrayField(evidence.evidenceRefs, `actionFlowRuntimeSimulationProof.mockEvidence[${index}].evidenceRefs`);
  }
  if (value.lifecycleEvidence !== undefined) {
    if (!Array.isArray(value.lifecycleEvidence)) rejectAppSpecStructure("actionFlowRuntimeSimulationProof.lifecycleEvidence must be an array");
    for (const [index, evidence] of value.lifecycleEvidence.entries()) {
      assertClosedEvidenceRecord(evidence, new Set(["bindingId", "flowName", "kind", "phase", "stageName", "order", "clockMs", "error", "evidenceRefs"]), `actionFlowRuntimeSimulationProof.lifecycleEvidence[${index}]`);
      if (
        !isNonEmptyString(evidence.bindingId) ||
        !isNonEmptyString(evidence.flowName) ||
        !["stage", "saga"].includes(String(evidence.kind)) ||
        !["onEnter", "steps", "onSuccess", "onError", "onFinally", "completed", "handled", "failed", "compensate"].includes(String(evidence.phase))
      ) rejectAppSpecStructure(`actionFlowRuntimeSimulationProof.lifecycleEvidence[${index}] is malformed`);
      assertFiniteNumber(evidence.order, `actionFlowRuntimeSimulationProof.lifecycleEvidence[${index}].order`);
      assertFiniteNumber(evidence.clockMs, `actionFlowRuntimeSimulationProof.lifecycleEvidence[${index}].clockMs`);
      assertStringArrayField(evidence.evidenceRefs, `actionFlowRuntimeSimulationProof.lifecycleEvidence[${index}].evidenceRefs`);
    }
  }
  assertClosedEvidenceRecord(value.summary, new Set(["bindingsSimulated", "flowsSimulated", "stepsSimulated", "stateWrites", "resultWrites", "datasourceMocks", "sideEffectMocks", "stagesCompleted", "compensationsAttempted", "motionTimeMs"]), "actionFlowRuntimeSimulationProof.summary");
  for (const field of ["bindingsSimulated", "flowsSimulated", "stepsSimulated", "stateWrites", "resultWrites", "datasourceMocks", "sideEffectMocks"]) assertFiniteNumber(value.summary[field], `actionFlowRuntimeSimulationProof.summary.${field}`);
}

function assertNonNegativeFiniteNumber(value: unknown, label: string): void {
  assertFiniteNumber(value, label);
  if ((value as number) < 0) rejectAppSpecStructure(`${label} must be nonnegative`);
}

function assertClosedEnum(value: unknown, allowed: readonly string[], label: string): void {
  if (!allowed.includes(String(value))) rejectAppSpecStructure(`${label} has an invalid value`);
}

function assertEvidenceFinding(value: unknown, label: string): void {
  assertClosedEvidenceRecord(value, new Set(["code", "severity", "message", "sample"]), label);
  if (!isNonEmptyString(value.code) || !["pass", "partial", "blocked"].includes(String(value.severity)) || !isNonEmptyString(value.message)) {
    rejectAppSpecStructure(`${label} is malformed`);
  }
  assertStringArrayField(value.sample, `${label}.sample`);
}

function assertRenderEngineEvidence(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["status", "denominator", "fullyCovered", "drift", "engines", "sources"]), "generationEvidence.renderEngine");
  if (value.status !== "pass") rejectAppSpecStructure("generationEvidence.renderEngine.status must be pass");
  for (const field of ["denominator", "fullyCovered", "drift"] as const) assertNonNegativeFiniteNumber(value[field], `generationEvidence.renderEngine.${field}`);
  assertClosedEvidenceRecord(value.engines, new Set(["web", "rn", "flutter"]), "generationEvidence.renderEngine.engines");
  for (const target of ["web", "rn"] as const) {
    const engine = value.engines[target];
    assertClosedEvidenceRecord(engine, new Set(["bound", "missing"]), `generationEvidence.renderEngine.engines.${target}`);
    assertNonNegativeFiniteNumber(engine.bound, `generationEvidence.renderEngine.engines.${target}.bound`);
    assertNonNegativeFiniteNumber(engine.missing, `generationEvidence.renderEngine.engines.${target}.missing`);
  }
  assertClosedEvidenceRecord(value.engines.flutter, new Set(["bound", "missing", "direct", "subpart"]), "generationEvidence.renderEngine.engines.flutter");
  for (const field of ["bound", "missing", "direct", "subpart"] as const) assertNonNegativeFiniteNumber(value.engines.flutter[field], `generationEvidence.renderEngine.engines.flutter.${field}`);
  assertStringArrayField(value.sources, "generationEvidence.renderEngine.sources");
}

function assertActionFlowContractEvidence(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["status", "executorSurface", "tboxParamDrift", "longTailParamCapabilities", "registrySchemaVersion", "capabilityCount", "generationAllowedCount", "directAtomicActionCount", "sources"]), "generationEvidence.actionflow");
  if (value.status !== "pass" || value.executorSurface !== "runtime-honored" || value.longTailParamCapabilities !== "drained" || !isNonEmptyString(value.registrySchemaVersion)) rejectAppSpecStructure("generationEvidence.actionflow is malformed");
  for (const field of ["tboxParamDrift", "capabilityCount", "generationAllowedCount", "directAtomicActionCount"] as const) assertNonNegativeFiniteNumber(value[field], `generationEvidence.actionflow.${field}`);
  assertStringArrayField(value.sources, "generationEvidence.actionflow.sources");
}

function assertCoverageEvidence(value: unknown, exhaustive: boolean): void {
  const label = exhaustive ? "generationEvidence.exhaustiveProof" : "generationEvidence.fullCoverage";
  const numericFields = exhaustive
    ? ["ontologyComponentCount", "styleCount", "contractProbeCount", "renderPackProbeCount", "ssrRenderedComponentCount", "soloSsrRenderedComponentCount", "compoundSsrRenderedComponentCount", "ssrMissingComponentCount", "providerSlotHydratedComponentCount", "providerSlotHydrationRequiredComponentCount", "runtimeHonoredActionCount", "runtimeExecutedActionCount", "runtimeAdapterProvenActionCount", "runtimeProvenActionCount", "runtimeAdapterRequiredActionCount", "runtimeNotExecutedActionCount", "blockingFindingCount", "partialFindingCount"]
    : ["componentOntologyCount", "actionOntologyCount", "rendererFullyCoveredCount", "componentCapabilityCatalogCount", "componentCapabilityDenominator", "bundledStyleCount", "flatbufferMappedComponentCount", "blockingFindingCount"];
  const allowed = exhaustive
    ? new Set(["schemaVersion", "status", "provenance", ...numericFields, "providerSlotHydrationProofStatus", "collapsedStyleSelectors", "findings", "sources"])
    : new Set(["schemaVersion", "status", "provenance", ...numericFields, "componentCapabilityPlatformCoverage", "componentCapabilityHighRiskComponents", "sectionStatuses", "findings", "sources"]);
  assertClosedEvidenceRecord(value, allowed, label);
  if (value.schemaVersion !== (exhaustive ? "studio-exhaustive-proof-evidence.v1" : "studio-full-coverage-evidence.v1")) rejectAppSpecStructure(`${label} has an invalid schemaVersion`);
  assertClosedEnum(value.status, ["pass", "partial", "blocked", "missing"], `${label}.status`);
  if (value.provenance !== undefined) assertClosedEnum(value.provenance, ["studio-input", "app-document"], `${label}.provenance`);
  for (const field of numericFields) assertNonNegativeFiniteNumber(value[field], `${label}.${field}`);
  if (exhaustive) {
    assertClosedEnum(value.providerSlotHydrationProofStatus, ["pass", "partial", "blocked", "missing"], `${label}.providerSlotHydrationProofStatus`);
    assertStringArrayField(value.collapsedStyleSelectors, `${label}.collapsedStyleSelectors`);
  } else {
    assertClosedEvidenceRecord(value.componentCapabilityPlatformCoverage, new Set(["webSupported", "rnSupported", "flutterSupported", "renderPackComponents"]), `${label}.componentCapabilityPlatformCoverage`);
    for (const field of ["webSupported", "rnSupported", "flutterSupported", "renderPackComponents"] as const) assertNonNegativeFiniteNumber(value.componentCapabilityPlatformCoverage[field], `${label}.componentCapabilityPlatformCoverage.${field}`);
    assertStringArrayField(value.componentCapabilityHighRiskComponents, `${label}.componentCapabilityHighRiskComponents`);
    assertClosedEvidenceRecord(value.sectionStatuses, new Set(["uiElements", "renderers", "componentCapabilities", "styleSeparation", "actions", "flatbuffer"]), `${label}.sectionStatuses`);
    for (const field of ["uiElements", "renderers", "componentCapabilities", "styleSeparation", "actions", "flatbuffer"] as const) assertClosedEnum(value.sectionStatuses[field], ["pass", "partial", "blocked", "missing"], `${label}.sectionStatuses.${field}`);
  }
  if (!Array.isArray(value.findings)) rejectAppSpecStructure(`${label}.findings must be an array`);
  value.findings.forEach((finding, index) => assertEvidenceFinding(finding, `${label}.findings[${index}]`));
  assertStringArrayField(value.sources, `${label}.sources`);
}

function assertNativeTargetProofEvidence(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "status", "provenance", "targets", "sources"]), "generationEvidence.nativeTargetProof");
  if (value.schemaVersion !== "studio-native-target-proof.v1") rejectAppSpecStructure("generationEvidence.nativeTargetProof has an invalid schemaVersion");
  assertClosedEnum(value.status, ["pass", "partial", "blocked", "missing"], "generationEvidence.nativeTargetProof.status");
  if (value.provenance !== undefined) assertClosedEnum(value.provenance, ["studio-input", "app-document"], "generationEvidence.nativeTargetProof.provenance");
  assertClosedEvidenceRecord(value.targets, new Set(["rn", "flutter"]), "generationEvidence.nativeTargetProof.targets");
  for (const target of ["rn", "flutter"] as const) {
    const proof = value.targets[target];
    assertClosedEvidenceRecord(proof, new Set(["status", "nativeRuntimeParity", "nativeLivePackaging", "r2VisualFaithfulness", "evidence", "reasons"]), `generationEvidence.nativeTargetProof.targets.${target}`);
    assertClosedEnum(proof.status, ["pass", "partial", "blocked", "missing"], `generationEvidence.nativeTargetProof.targets.${target}.status`);
    for (const field of ["nativeRuntimeParity", "nativeLivePackaging", "r2VisualFaithfulness"] as const) assertClosedEnum(proof[field], ["pass", "partial", "pending", "not-applicable"], `generationEvidence.nativeTargetProof.targets.${target}.${field}`);
    assertStringArrayField(proof.evidence, `generationEvidence.nativeTargetProof.targets.${target}.evidence`);
    assertStringArrayField(proof.reasons, `generationEvidence.nativeTargetProof.targets.${target}.reasons`);
  }
  assertStringArrayField(value.sources, "generationEvidence.nativeTargetProof.sources");
}

function assertApplicationConformanceOracle(value: unknown, label = "generationEvidence.applicationConformanceOracle"): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "sourceSchemaVersion", "status", "required", "blockCount", "checkedBlockNames", "dimensions", "blockedReasons", "notes"]), label);
  if (value.schemaVersion !== "studio-application-conformance-oracle.v1" || (value.sourceSchemaVersion !== undefined && value.sourceSchemaVersion !== "cogo.application-conformance-oracle.v1") || typeof value.required !== "boolean") rejectAppSpecStructure(`${label} is malformed`);
  assertClosedEnum(value.status, ["pass", "blocked", "missing"], `${label}.status`);
  assertNonNegativeFiniteNumber(value.blockCount, `${label}.blockCount`);
  assertStringArrayField(value.checkedBlockNames, `${label}.checkedBlockNames`);
  if (!Array.isArray(value.dimensions)) rejectAppSpecStructure(`${label}.dimensions must be an array`);
  value.dimensions.forEach((dimension, index) => {
    assertClosedEvidenceRecord(dimension, new Set(["dimension", "status", "checkedCount", "divergenceCount", "firstDivergenceReason"]), `${label}.dimensions[${index}]`);
    if (!isNonEmptyString(dimension.dimension)) rejectAppSpecStructure(`${label}.dimensions[${index}].dimension is malformed`);
    assertClosedEnum(dimension.status, ["pass", "blocked", "missing"], `${label}.dimensions[${index}].status`);
    for (const field of ["checkedCount", "divergenceCount"] as const) assertNonNegativeFiniteNumber(dimension[field], `${label}.dimensions[${index}].${field}`);
    if (dimension.firstDivergenceReason !== undefined && !isNonEmptyString(dimension.firstDivergenceReason)) rejectAppSpecStructure(`${label}.dimensions[${index}].firstDivergenceReason is malformed`);
  });
  assertStringArrayField(value.blockedReasons, `${label}.blockedReasons`);
  assertStringArrayField(value.notes, `${label}.notes`);
}

function assertGeneratedAppEvidence(value: unknown): void {
  const label = "generationEvidence.generatedApp";
  assertClosedEvidenceRecord(value, new Set(["status", "appClass", "flowCount", "eventBindingCount", "datasourceCount", "stateVariableCount", "localStateVariableCount", "globalStateVariableCount", "stateIsolation", "actionFlowRequired", "artifactParity", "styleSeparation", "reportApplicationShell", "applicationConformance", "visibleFromJson", "imageEvidenceVerification", "imageEvidenceCandidateAdmission", "notes"]), label);
  assertClosedEnum(value.status, ["pending", "pass", "partial", "not-required"], `${label}.status`);
  assertClosedEnum(value.appClass, ["static", "dynamic", "hybrid", "unknown"], `${label}.appClass`);
  for (const field of ["flowCount", "eventBindingCount", "datasourceCount", "stateVariableCount", "localStateVariableCount", "globalStateVariableCount"] as const) assertNonNegativeFiniteNumber(value[field], `${label}.${field}`);
  assertClosedEnum(value.stateIsolation, ["pass", "partial", "not-required"], `${label}.stateIsolation`);
  if (typeof value.actionFlowRequired !== "boolean") rejectAppSpecStructure(`${label}.actionFlowRequired must be boolean`);
  assertClosedEvidenceRecord(value.artifactParity, new Set(["status", "manifestPresent", "blockCount", "pageCount", "missingPageBlockRefs", "targetPlatforms", "notes"]), `${label}.artifactParity`);
  assertClosedEnum(value.artifactParity.status, ["pending", "pass", "partial", "not-applicable"], `${label}.artifactParity.status`);
  if (typeof value.artifactParity.manifestPresent !== "boolean") rejectAppSpecStructure(`${label}.artifactParity.manifestPresent must be boolean`);
  for (const field of ["blockCount", "pageCount"] as const) assertNonNegativeFiniteNumber(value.artifactParity[field], `${label}.artifactParity.${field}`);
  assertStringArrayField(value.artifactParity.missingPageBlockRefs, `${label}.artifactParity.missingPageBlockRefs`);
  if (!Array.isArray(value.artifactParity.targetPlatforms) || !value.artifactParity.targetPlatforms.every((target) => ["web", "rn", "flutter", "mobile", "desktop"].includes(String(target)))) rejectAppSpecStructure(`${label}.artifactParity.targetPlatforms is malformed`);
  assertStringArrayField(value.artifactParity.notes, `${label}.artifactParity.notes`);
  assertApplicationConformanceOracle(value.applicationConformance, `${label}.applicationConformance`);
  assertClosedEvidenceRecord(value.styleSeparation, new Set(["schemaVersion", "status", "checkedBlockCount", "blockedBlockCount", "findingCount", "visualClassNameTokenCount", "rawStyleValueCount", "inlineStyleObjectCount", "inlineVisualStyleCount", "unresolvedDesignAuthorityCount", "reports", "notes"]), `${label}.styleSeparation`);
  if (value.styleSeparation.schemaVersion !== "studio-generated-app-style-separation.v1") rejectAppSpecStructure(`${label}.styleSeparation has an invalid schemaVersion`);
  assertClosedEnum(value.styleSeparation.status, ["pending", "pass", "partial"], `${label}.styleSeparation.status`);
  for (const field of ["checkedBlockCount", "blockedBlockCount", "findingCount", "visualClassNameTokenCount", "rawStyleValueCount", "inlineStyleObjectCount", "inlineVisualStyleCount", "unresolvedDesignAuthorityCount"] as const) assertNonNegativeFiniteNumber(value.styleSeparation[field], `${label}.styleSeparation.${field}`);
  if (!Array.isArray(value.styleSeparation.reports)) rejectAppSpecStructure(`${label}.styleSeparation.reports must be an array`);
  value.styleSeparation.reports.forEach((report, index) => {
    assertClosedEvidenceRecord(report, new Set(["blockId", "status", "findingCount", "summary", "firstFinding"]), `${label}.styleSeparation.reports[${index}]`);
    if (!isNonEmptyString(report.blockId)) rejectAppSpecStructure(`${label}.styleSeparation.reports[${index}].blockId is malformed`);
    assertClosedEnum(report.status, ["pass", "partial", "blocked"], `${label}.styleSeparation.reports[${index}].status`);
    assertNonNegativeFiniteNumber(report.findingCount, `${label}.styleSeparation.reports[${index}].findingCount`);
    if (!isRecord(report.summary)) rejectAppSpecStructure(`${label}.styleSeparation.reports[${index}].summary must be an object`);
    if (report.firstFinding !== undefined) {
      assertClosedEvidenceRecord(report.firstFinding, new Set(["rule", "severity", "path", "token", "key"]), `${label}.styleSeparation.reports[${index}].firstFinding`);
      if (![report.firstFinding.rule, report.firstFinding.severity, report.firstFinding.path].every(isNonEmptyString)) rejectAppSpecStructure(`${label}.styleSeparation.reports[${index}].firstFinding is malformed`);
      for (const field of ["token", "key"] as const) if (report.firstFinding[field] !== undefined && !isNonEmptyString(report.firstFinding[field])) rejectAppSpecStructure(`${label}.styleSeparation.reports[${index}].firstFinding.${field} is malformed`);
    }
  });
  assertStringArrayField(value.styleSeparation.notes, `${label}.styleSeparation.notes`);
  assertClosedEvidenceRecord(value.reportApplicationShell, new Set(["schemaVersion", "status", "applicable", "findingCount", "summary", "firstFinding", "notes"]), `${label}.reportApplicationShell`);
  if (value.reportApplicationShell.schemaVersion !== "studio-generated-app-report-application-shell.v1" || typeof value.reportApplicationShell.applicable !== "boolean") rejectAppSpecStructure(`${label}.reportApplicationShell is malformed`);
  assertClosedEnum(value.reportApplicationShell.status, ["not-applicable", "pending", "pass", "partial"], `${label}.reportApplicationShell.status`);
  assertNonNegativeFiniteNumber(value.reportApplicationShell.findingCount, `${label}.reportApplicationShell.findingCount`);
  assertClosedEvidenceRecord(value.reportApplicationShell.summary, new Set(["nodeCount", "globalCommandHeaderCount", "chatCommandInputCount", "reportCommonControlsInHeaderCount", "collapsibleSidebarCount", "fullHeightSidebarCount", "a4PortraitCanvasCount", "reportNarrativeCount", "reportChartSurfaceCount", "reportDataTableCount"]), `${label}.reportApplicationShell.summary`);
  for (const field of ["nodeCount", "globalCommandHeaderCount", "chatCommandInputCount", "reportCommonControlsInHeaderCount", "collapsibleSidebarCount", "fullHeightSidebarCount", "a4PortraitCanvasCount", "reportNarrativeCount", "reportChartSurfaceCount", "reportDataTableCount"] as const) assertNonNegativeFiniteNumber(value.reportApplicationShell.summary[field], `${label}.reportApplicationShell.summary.${field}`);
  if (value.reportApplicationShell.firstFinding !== undefined) {
    assertClosedEvidenceRecord(value.reportApplicationShell.firstFinding, new Set(["rule", "severity", "path"]), `${label}.reportApplicationShell.firstFinding`);
    if (![value.reportApplicationShell.firstFinding.rule, value.reportApplicationShell.firstFinding.severity, value.reportApplicationShell.firstFinding.path].every(isNonEmptyString)) rejectAppSpecStructure(`${label}.reportApplicationShell.firstFinding is malformed`);
  }
  assertStringArrayField(value.reportApplicationShell.notes, `${label}.reportApplicationShell.notes`);
  assertVisibleFromJsonOracle(value.visibleFromJson, `${label}.visibleFromJson`);
  assertGeneratedUiVerificationSummary(value.imageEvidenceVerification, `${label}.imageEvidenceVerification`);
  if (value.imageEvidenceCandidateAdmission !== undefined) assertImageEvidenceCandidateAdmission(value.imageEvidenceCandidateAdmission, `${label}.imageEvidenceCandidateAdmission`);
  assertStringArrayField(value.notes, `${label}.notes`);
}

function assertVisibleFromJsonOracle(value: unknown, label: string): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "sourceSchemaVersion", "status", "required", "checkedSurface", "declaredSlotCount", "visibleSlotCount", "hiddenSlotCount", "missingSlotCount", "rootRasterOnly", "rasterAuthorityDetected", "blockedReasons", "checkedSlots", "notes"]), label);
  if (value.schemaVersion !== "studio-visible-from-json-oracle.v1" || (value.sourceSchemaVersion !== undefined && value.sourceSchemaVersion !== "cogo.visible-from-json-oracle.v1") || typeof value.required !== "boolean" || typeof value.rootRasterOnly !== "boolean" || typeof value.rasterAuthorityDetected !== "boolean") rejectAppSpecStructure(`${label} is malformed`);
  assertClosedEnum(value.status, ["pass", "blocked", "missing"], `${label}.status`);
  assertClosedEnum(value.checkedSurface, ["cogo-json-render-tree", "missing"], `${label}.checkedSurface`);
  for (const field of ["declaredSlotCount", "visibleSlotCount", "hiddenSlotCount", "missingSlotCount"] as const) assertNonNegativeFiniteNumber(value[field], `${label}.${field}`);
  assertStringArrayField(value.blockedReasons, `${label}.blockedReasons`);
  if (!Array.isArray(value.checkedSlots)) rejectAppSpecStructure(`${label}.checkedSlots must be an array`);
  value.checkedSlots.forEach((slot, index) => {
    assertClosedEvidenceRecord(slot, new Set(["slotId", "nodeId", "label", "value", "required", "foundTextNodeCount", "visibleTextNodeCount", "status", "firstReason"]), `${label}.checkedSlots[${index}]`);
    if (!isNonEmptyString(slot.value) || typeof slot.required !== "boolean") rejectAppSpecStructure(`${label}.checkedSlots[${index}] is malformed`);
    for (const field of ["slotId", "nodeId", "label", "firstReason"] as const) if (slot[field] !== undefined && !isNonEmptyString(slot[field])) rejectAppSpecStructure(`${label}.checkedSlots[${index}].${field} is malformed`);
    for (const field of ["foundTextNodeCount", "visibleTextNodeCount"] as const) assertNonNegativeFiniteNumber(slot[field], `${label}.checkedSlots[${index}].${field}`);
    assertClosedEnum(slot.status, ["pass", "blocked"], `${label}.checkedSlots[${index}].status`);
  });
  assertStringArrayField(value.notes, `${label}.notes`);
}

function assertGeneratedUiVerificationSummary(value: unknown, label: string): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "enabled", "status", "surfaceCount", "promotionAllowed", "canPublish", "requiredEvidence", "blockedReasons", "surfaces"]), label);
  if (value.schemaVersion !== "cogo.generated-ui-verification-summary.v1" || typeof value.enabled !== "boolean" || typeof value.promotionAllowed !== "boolean" || typeof value.canPublish !== "boolean") rejectAppSpecStructure(`${label} is malformed`);
  assertClosedEnum(value.status, ["pass", "blocked", "skipped"], `${label}.status`);
  assertNonNegativeFiniteNumber(value.surfaceCount, `${label}.surfaceCount`);
  for (const field of ["requiredEvidence", "blockedReasons"] as const) assertStringArrayField(value[field], `${label}.${field}`);
  if (!Array.isArray(value.surfaces)) rejectAppSpecStructure(`${label}.surfaces must be an array`);
  value.surfaces.forEach((surface, index) => {
    assertClosedEvidenceRecord(surface, new Set(["surfaceId", "provenanceTier", "verificationLevel", "promotionAllowed", "gates", "issues"]), `${label}.surfaces[${index}]`);
    if (!isNonEmptyString(surface.surfaceId) || typeof surface.promotionAllowed !== "boolean") rejectAppSpecStructure(`${label}.surfaces[${index}] is malformed`);
    assertClosedEnum(surface.provenanceTier, ["ontology-direct-reuse", "ui-block-context-changed", "design-system-generated", "image-derived-candidate", "lower-layer-generated-candidate"], `${label}.surfaces[${index}].provenanceTier`);
    assertClosedEnum(surface.verificationLevel, ["provenance-only", "lightweight", "medium", "strict", "strictest"], `${label}.surfaces[${index}].verificationLevel`);
    if (!Array.isArray(surface.gates)) rejectAppSpecStructure(`${label}.surfaces[${index}].gates must be an array`);
    surface.gates.forEach((gate, gateIndex) => {
      assertClosedEvidenceRecord(gate, new Set(["gate", "status", "detail"]), `${label}.surfaces[${index}].gates[${gateIndex}]`);
      if (!isNonEmptyString(gate.gate) || !isNonEmptyString(gate.detail)) rejectAppSpecStructure(`${label}.surfaces[${index}].gates[${gateIndex}] is malformed`);
      assertClosedEnum(gate.status, ["pass", "blocked", "skipped"], `${label}.surfaces[${index}].gates[${gateIndex}].status`);
    });
    assertStringArrayField(surface.issues, `${label}.surfaces[${index}].issues`);
  });
}

function assertImageEvidenceCandidateAdmission(value: unknown, label: string): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "candidateId", "sourceTier", "status", "canSeedOntology", "canPublishFromCurrentGeneration", "requiredEvidence", "blockedReasons", "notes", "review"]), label);
  if (value.schemaVersion !== "cogo.image-evidence-candidate-admission.v1" || !isNonEmptyString(value.candidateId) || value.sourceTier !== "image-derived-candidate" || typeof value.canSeedOntology !== "boolean" || value.canPublishFromCurrentGeneration !== false) rejectAppSpecStructure(`${label} is malformed`);
  assertClosedEnum(value.status, ["review-required", "admitted", "rejected", "quarantined", "blocked"], `${label}.status`);
  assertStringArrayField(value.requiredEvidence, `${label}.requiredEvidence`);
  assertStringArrayField(value.blockedReasons, `${label}.blockedReasons`);
  if (value.notes !== undefined) assertStringArrayField(value.notes, `${label}.notes`);
  if (value.review !== undefined) {
    assertClosedEvidenceRecord(value.review, new Set(["decision", "reviewedAt", "reviewerKind", "evidenceArtifacts", "visualDiffScore", "generatedCogoDigest", "notes"]), `${label}.review`);
    assertClosedEnum(value.review.decision, ["admit", "reject", "quarantine"], `${label}.review.decision`);
    assertIsoTimestamp(value.review.reviewedAt, `${label}.review.reviewedAt`);
    assertClosedEnum(value.review.reviewerKind, ["operator", "automated-oracle"], `${label}.review.reviewerKind`);
    assertStringArrayField(value.review.evidenceArtifacts, `${label}.review.evidenceArtifacts`);
    if (value.review.visualDiffScore !== undefined) assertNonNegativeFiniteNumber(value.review.visualDiffScore, `${label}.review.visualDiffScore`);
    if (value.review.generatedCogoDigest !== undefined && !isNonEmptyString(value.review.generatedCogoDigest)) rejectAppSpecStructure(`${label}.review.generatedCogoDigest is malformed`);
    if (value.review.notes !== undefined) assertStringArrayField(value.review.notes, `${label}.review.notes`);
  }
}

function assertActionFlowReadiness(value: unknown): void {
  assertClosedEvidenceRecord(value, new Set(["schemaVersion", "contractVersion", "status", "required", "generatedAt", "evidenceRefs", "reasons", "ontologyValidation", "headlessValidation", "headlessSimulation", "userOutcomeProof", "browserTraceProof", "readinessPacket", "summary"]), "actionFlowReadiness");
  if (
    value.schemaVersion !== "studio-actionflow-readiness.v1" ||
    value.contractVersion !== "studio-actionflow-state-topology.v1" ||
    !["ready", "gap", "fallback", "invalid", "not-required"].includes(String(value.status)) ||
    typeof value.required !== "boolean"
  ) rejectAppSpecStructure("actionFlowReadiness does not match its canonical contract");
  assertIsoTimestamp(value.generatedAt, "actionFlowReadiness.generatedAt");
  assertStringArrayField(value.evidenceRefs, "actionFlowReadiness.evidenceRefs");
  assertStringArrayField(value.reasons, "actionFlowReadiness.reasons");
  assertClosedEvidenceRecord(value.ontologyValidation, new Set(["ok", "errors"]), "actionFlowReadiness.ontologyValidation");
  if (typeof value.ontologyValidation.ok !== "boolean") rejectAppSpecStructure("actionFlowReadiness.ontologyValidation.ok must be boolean");
  assertStringArrayField(value.ontologyValidation.errors, "actionFlowReadiness.ontologyValidation.errors");
  assertClosedEvidenceRecord(value.summary, new Set(["topologyValid", "compilerValid", "runtimeValid", "headlessValidationValid", "headlessSimulationValid", "userOutcomeValid", "browserTraceValid", "headlessPublishAllowed", "primitiveCount", "compilerParamBindingCount", "runtimeFlowsSimulated", "actionFlowOntologyPlanStatus", "actionFlowOntologyGapCount"]), "actionFlowReadiness.summary");
  for (const field of ["topologyValid", "compilerValid", "runtimeValid"]) if (typeof value.summary[field] !== "boolean") rejectAppSpecStructure(`actionFlowReadiness.summary.${field} must be boolean`);
  for (const field of ["primitiveCount", "compilerParamBindingCount", "runtimeFlowsSimulated"]) assertFiniteNumber(value.summary[field], `actionFlowReadiness.summary.${field}`);
  if (value.headlessValidation !== undefined) {
    assertClosedEvidenceRecord(value.headlessValidation, new Set(["ok", "errors"]), "actionFlowReadiness.headlessValidation");
    if (typeof value.headlessValidation.ok !== "boolean") rejectAppSpecStructure("actionFlowReadiness.headlessValidation.ok must be boolean");
    assertStringArrayField(value.headlessValidation.errors, "actionFlowReadiness.headlessValidation.errors");
  }
  if (value.headlessSimulation !== undefined) {
    assertClosedEvidenceRecord(value.headlessSimulation, new Set(["schemaVersion", "ok", "validationErrors", "errors", "summary"]), "actionFlowReadiness.headlessSimulation");
    if (!isNonEmptyString(value.headlessSimulation.schemaVersion) || typeof value.headlessSimulation.ok !== "boolean" || !isRecord(value.headlessSimulation.summary)) rejectAppSpecStructure("actionFlowReadiness.headlessSimulation is malformed");
    assertStringArrayField(value.headlessSimulation.validationErrors, "actionFlowReadiness.headlessSimulation.validationErrors");
    assertStringArrayField(value.headlessSimulation.errors, "actionFlowReadiness.headlessSimulation.errors");
    for (const [key, count] of Object.entries(value.headlessSimulation.summary)) assertFiniteNumber(count, `actionFlowReadiness.headlessSimulation.summary.${key}`);
  }
  for (const field of ["userOutcomeProof", "browserTraceProof"] as const) {
    const proof = value[field];
    if (proof === undefined) continue;
    const browser = field === "browserTraceProof";
    assertClosedEvidenceRecord(proof, new Set(browser
      ? ["schemaVersion", "contractVersion", "scenarioId", "ok", "readiness", "triggerLabel", "evaluationCount", "blockingFailures", "evidenceRefs"]
      : ["schemaVersion", "contractVersion", "scenarioId", "ok", "readiness", "outcomeCount", "blockingFailures", "evidenceRefs"]), `actionFlowReadiness.${field}`);
    if (!isNonEmptyString(proof.schemaVersion) || !isNonEmptyString(proof.contractVersion) || !isNonEmptyString(proof.scenarioId) || typeof proof.ok !== "boolean" || !["ready", "blocked"].includes(String(proof.readiness))) rejectAppSpecStructure(`actionFlowReadiness.${field} is malformed`);
    assertFiniteNumber(proof[browser ? "evaluationCount" : "outcomeCount"], `actionFlowReadiness.${field}.count`);
    assertStringArrayField(proof.blockingFailures, `actionFlowReadiness.${field}.blockingFailures`);
    assertStringArrayField(proof.evidenceRefs, `actionFlowReadiness.${field}.evidenceRefs`);
  }
  if (value.readinessPacket !== undefined) {
    const packet = value.readinessPacket;
    assertClosedEvidenceRecord(packet, new Set(["schemaVersion", "status", "publishAllowed", "stages", "blockingFailures", "evidenceRefs", "policy"]), "actionFlowReadiness.readinessPacket");
    if (!isNonEmptyString(packet.schemaVersion) || !["ready", "blocked"].includes(String(packet.status)) || typeof packet.publishAllowed !== "boolean" || !Array.isArray(packet.stages) || !isRecord(packet.policy)) rejectAppSpecStructure("actionFlowReadiness.readinessPacket is malformed");
    assertStringArrayField(packet.blockingFailures, "actionFlowReadiness.readinessPacket.blockingFailures");
    assertStringArrayField(packet.evidenceRefs, "actionFlowReadiness.readinessPacket.evidenceRefs");
    for (const [key, flag] of Object.entries(packet.policy)) if (typeof flag !== "boolean") rejectAppSpecStructure(`actionFlowReadiness.readinessPacket.policy.${key} must be boolean`);
    for (const [index, stage] of packet.stages.entries()) {
      assertClosedEvidenceRecord(stage, new Set(["id", "status", "blocking", "summary", "failures", "evidenceRefs"]), `actionFlowReadiness.readinessPacket.stages[${index}]`);
      if (!isNonEmptyString(stage.id) || !["ready", "blocked", "missing"].includes(String(stage.status)) || typeof stage.blocking !== "boolean" || typeof stage.summary !== "string") rejectAppSpecStructure(`actionFlowReadiness.readinessPacket.stages[${index}] is malformed`);
      assertStringArrayField(stage.failures, `actionFlowReadiness.readinessPacket.stages[${index}].failures`);
      assertStringArrayField(stage.evidenceRefs, `actionFlowReadiness.readinessPacket.stages[${index}].evidenceRefs`);
    }
  }
}

function assertCanonicalGenerationAndActionFlowEvidence(appSpec: AppSpecFull): void {
  if (appSpec.generationEvidence !== undefined) {
    const evidence = appSpec.generationEvidence;
    assertClosedEvidenceRecord(evidence, new Set(["schemaVersion", "uiOntology", "renderEngine", "actionflow", "fullCoverage", "exhaustiveProof", "nativeTargetProof", "applicationConformanceOracle", "generatedApp", "productionEvidenceChain", "endUserAppProof", "generatedAt"]), "generationEvidence");
    if (evidence.schemaVersion !== "studio-generation-evidence.v1") rejectAppSpecStructure("generationEvidence has an invalid schemaVersion");
    assertIsoTimestamp(evidence.generatedAt, "generationEvidence.generatedAt");
    for (const field of ["uiOntology", "renderEngine", "actionflow", "fullCoverage", "exhaustiveProof", "nativeTargetProof", "applicationConformanceOracle", "generatedApp"]) {
      if (!isRecord(evidence[field])) rejectAppSpecStructure(`generationEvidence.${field} must be an object`);
    }
    assertClosedEvidenceRecord(evidence.uiOntology, new Set(["status", "mode", "targetPlatform", "runtimeTargets", "adaptiveRuntime", "goldenRecipes", "rendererBlankTypes", "preflight", "sources"]), "generationEvidence.uiOntology");
    if (!['pass', 'blocked', 'fallback'].includes(String(evidence.uiOntology.status)) || !['ontology-driven', 'llm-fallback', 'blocked'].includes(String(evidence.uiOntology.mode)) || !['react', 'flutter'].includes(String(evidence.uiOntology.targetPlatform)) || !isRecord(evidence.uiOntology.adaptiveRuntime) || !isRecord(evidence.uiOntology.goldenRecipes) || !isRecord(evidence.uiOntology.preflight)) rejectAppSpecStructure("generationEvidence.uiOntology is malformed");
    assertStringArrayField(evidence.uiOntology.runtimeTargets, "generationEvidence.uiOntology.runtimeTargets");
    assertFiniteNumber(evidence.uiOntology.rendererBlankTypes, "generationEvidence.uiOntology.rendererBlankTypes");
    assertStringArrayField(evidence.uiOntology.sources, "generationEvidence.uiOntology.sources");
    assertRenderEngineEvidence(evidence.renderEngine);
    assertActionFlowContractEvidence(evidence.actionflow);
    assertCoverageEvidence(evidence.fullCoverage, false);
    assertCoverageEvidence(evidence.exhaustiveProof, true);
    assertNativeTargetProofEvidence(evidence.nativeTargetProof);
    assertApplicationConformanceOracle(evidence.applicationConformanceOracle);
    assertGeneratedAppEvidence(evidence.generatedApp);
    // These records are consumed by publish readiness. The only local reader
    // normalizes permissive input and therefore cannot establish a canonical,
    // immutable caller-supplied proof contract here. They must enter through
    // their dedicated trusted evidence pipeline after authority admission.
    if (evidence.productionEvidenceChain !== undefined || evidence.endUserAppProof !== undefined) {
      rejectAppSpecStructure("generationEvidence production and end-user publish proofs are not admitted from a caller AppSpec");
    }
  }
  if (appSpec.actionFlowTopology !== undefined) {
    assertActionFlowTopology(appSpec.actionFlowTopology, appSpec);
  }
  if (appSpec.actionFlowValidationProof !== undefined) assertActionFlowValidationProof(appSpec.actionFlowValidationProof);
  if (appSpec.actionFlowBindingPlan !== undefined) assertActionFlowBindingPlan(appSpec.actionFlowBindingPlan);
  if (appSpec.actionFlowCompilerProof !== undefined) assertActionFlowCompilerProof(appSpec.actionFlowCompilerProof);
  if (appSpec.actionFlowGenerationAuthority !== undefined) assertActionFlowGenerationAuthority(appSpec.actionFlowGenerationAuthority);
  if (appSpec.actionFlowRuntimeSimulationProof !== undefined) assertActionFlowRuntimeProof(appSpec.actionFlowRuntimeSimulationProof);
  if (appSpec.actionFlowReadiness !== undefined) assertActionFlowReadiness(appSpec.actionFlowReadiness);
  if (appSpec.actionFlowCompiledFlows !== undefined) {
    if (
      !isRecord(appSpec.actionFlowCompiledFlows) ||
      !Object.values(appSpec.actionFlowCompiledFlows).every(isRecord)
    ) {
      rejectAppSpecStructure("actionFlowCompiledFlows must be a canonical FlowMap object");
    }
  }
}

function assertFullApplicationSpec(
  appSpec: unknown,
): asserts appSpec is AppSpecFull {
  if (!isRecord(appSpec)) {
    rejectAppSpecStructure("root must be an object");
  }
  assertClosedApplicationSpecSurface(appSpec);
  if (!isNonEmptyString(appSpec.name)) {
    rejectAppSpecStructure("name must be a nonempty string");
  }
  if (!Array.isArray(appSpec.blocks)) {
    rejectAppSpecStructure("blocks must be an array");
  }
  for (const [index, block] of appSpec.blocks.entries()) {
    if (isRecord(block)) {
      rejectUnknownKeys(
        block,
        APP_SPEC_BLOCK_ALLOWED_KEYS,
        `blocks[${index}]`,
      );
    }
    if (
      !isRecord(block) ||
      !isNonEmptyString(block.name) ||
      !isNonEmptyString(block.type) ||
      typeof block.description !== "string" ||
      (block.binds !== undefined &&
        (!Array.isArray(block.binds) ||
          !block.binds.every((binding) => typeof binding === "string")))
    ) {
      rejectAppSpecStructure(
        `blocks[${index}] must contain name, type, description, and optional string binds`,
      );
    }
  }
  if (appSpec.pages !== undefined) {
    if (!Array.isArray(appSpec.pages)) {
      rejectAppSpecStructure("pages must be an array when provided");
    }
    for (const [index, page] of appSpec.pages.entries()) {
      if (isRecord(page)) {
        rejectUnknownKeys(
          page,
          APP_SPEC_PAGE_ALLOWED_KEYS,
          `pages[${index}]`,
        );
      }
      if (
        !isRecord(page) ||
        !isNonEmptyString(page.id) ||
        !isNonEmptyString(page.title) ||
        !isNonEmptyString(page.route) ||
        !Array.isArray(page.blocks) ||
        !page.blocks.every((block) => typeof block === "string")
      ) {
        rejectAppSpecStructure(
          `pages[${index}] must contain id, title, route, and string block references`,
        );
      }
    }
  }
  if (appSpec.datasources !== undefined) {
    if (!Array.isArray(appSpec.datasources)) {
      rejectAppSpecStructure("datasources must be an array when provided");
    }
    for (const [index, datasource] of appSpec.datasources.entries()) {
      if (isRecord(datasource)) {
        rejectUnknownKeys(
          datasource,
          APP_SPEC_DATASOURCE_ALLOWED_KEYS,
          `datasources[${index}]`,
        );
      }
      if (
        !isRecord(datasource) ||
        !isNonEmptyString(datasource.name) ||
        !isNonEmptyString(datasource.type) ||
        !isStringMap(datasource.schema)
      ) {
        rejectAppSpecStructure(
          `datasources[${index}] must contain name, type, and a string schema map`,
        );
      }
    }
  }
  if (appSpec.globalState !== undefined) {
    if (!isRecord(appSpec.globalState)) {
      rejectAppSpecStructure("globalState must be an object when provided");
    }
    for (const [name, declaration] of Object.entries(appSpec.globalState)) {
      if (isRecord(declaration)) {
        rejectUnknownKeys(
          declaration,
          APP_SPEC_GLOBAL_STATE_DECLARATION_ALLOWED_KEYS,
          `globalState.${name}`,
        );
      }
      if (!isRecord(declaration) || !isNonEmptyString(declaration.type)) {
        rejectAppSpecStructure(
          `globalState.${name} must contain a nonempty type and default`,
        );
      }
      if (!Object.prototype.hasOwnProperty.call(declaration, "default")) {
        rejectAppSpecStructure(`globalState.${name} must contain default`);
      }
    }
  }
  if (appSpec.interactionContract !== undefined) {
    const contract = appSpec.interactionContract;
    if (isRecord(contract)) {
      rejectUnknownKeys(
        contract,
        APP_SPEC_INTERACTION_CONTRACT_ALLOWED_KEYS,
        "interactionContract",
      );
    }
    if (
      !isRecord(contract) ||
      typeof contract.actionFlowRequired !== "boolean" ||
      !Array.isArray(contract.requiredEventTypes) ||
      !contract.requiredEventTypes.every((value) => typeof value === "string") ||
      !Array.isArray(contract.requiredStateScopes) ||
      !contract.requiredStateScopes.every((value) => typeof value === "string") ||
      typeof contract.requireResultsTrace !== "boolean" ||
      typeof contract.requireComputedProjection !== "boolean" ||
      !Array.isArray(contract.notes) ||
      !contract.notes.every((value) => typeof value === "string")
    ) {
      rejectAppSpecStructure(
        "interactionContract must match the canonical interaction contract schema",
      );
    }
  }
  if (appSpec.themeHint !== undefined) {
    const themeHint = appSpec.themeHint;
    if (isRecord(themeHint)) {
      rejectUnknownKeys(
        themeHint,
        APP_SPEC_THEME_HINT_ALLOWED_KEYS,
        "themeHint",
      );
    }
    if (
      !isRecord(themeHint) ||
      !isNonEmptyString(themeHint.primaryColor) ||
      !isNonEmptyString(themeHint.style) ||
      (themeHint.fontFamily !== undefined &&
        !isNonEmptyString(themeHint.fontFamily))
    ) {
      rejectAppSpecStructure(
        "themeHint must contain primaryColor, style, and an optional nonempty fontFamily",
      );
    }
  }
  for (const [field, value] of [
    ["designSystemTokens", appSpec.designSystemTokens],
    ["designSystem", appSpec.designSystem],
    ["designContract", appSpec.designContract],
  ] as const) {
    if (value !== undefined && !isStringMap(value)) {
      rejectAppSpecStructure(`${field} must be a string map`);
    }
  }
  if (
    appSpec.designSystemContract !== undefined &&
    !isRecord(appSpec.designSystemContract)
  ) {
    rejectAppSpecStructure("designSystemContract must be an object");
  }
  if (appSpec.projectPlan !== undefined) {
    assertCanonicalProjectPlan(appSpec.projectPlan);
  }
  if (appSpec.goldenRecipePack !== undefined) {
    assertCanonicalGoldenRecipePack(appSpec.goldenRecipePack);
  }
  if (!hasFullApplicationSpecRequiredFields(appSpec)) {
    rejectAppSpecStructure("root does not satisfy the required AppSpec identity");
  }
  assertCanonicalGenerationAndActionFlowEvidence(appSpec);
}

function assertClosedApplicationSpecSurface(
  appSpec: Record<string, unknown>,
): void {
  rejectUnknownKeys(appSpec, APP_SPEC_FULL_ALLOWED_KEYS, "root");
  if (isRecord(appSpec.interactionContract)) {
    rejectUnknownKeys(
      appSpec.interactionContract,
      APP_SPEC_INTERACTION_CONTRACT_ALLOWED_KEYS,
      "interactionContract",
    );
  }
  if (isRecord(appSpec.globalState)) {
    for (const [name, declaration] of Object.entries(appSpec.globalState)) {
      if (isRecord(declaration)) {
        rejectUnknownKeys(
          declaration,
          APP_SPEC_GLOBAL_STATE_DECLARATION_ALLOWED_KEYS,
          `globalState.${name}`,
        );
      }
    }
  }
  for (const [field, allowed] of [
    ["pages", APP_SPEC_PAGE_ALLOWED_KEYS],
    ["blocks", APP_SPEC_BLOCK_ALLOWED_KEYS],
    ["datasources", APP_SPEC_DATASOURCE_ALLOWED_KEYS],
  ] as const) {
    const entries = appSpec[field];
    if (!Array.isArray(entries)) continue;
    for (const [index, entry] of entries.entries()) {
      if (isRecord(entry)) rejectUnknownKeys(entry, allowed, `${field}[${index}]`);
    }
  }
}

function assertResponsibilityCoverage(appSpec: AppSpecFull): void {
  const report = assertApplicationResponsibilityCoverage(appSpec);
  if (!report.valid) {
    throw new Error(
      `Application responsibility coverage is incomplete: ${report.issues.map((issue) => issue.code).join(", ")}`,
    );
  }
}

type PartialProvidedApplicationAuthority = Extract<
  StudioProvidedApplicationAuthority,
  { mode: "partial-provided-spec" }
>;

interface ValidatedPartialAuthorityPointers {
  lockedPaths: BoundedApplicationPointer[];
  openSlots: BoundedApplicationPointer[];
}

function validatePartialAuthorityFacts(
  authority: PartialProvidedApplicationAuthority,
): ValidatedPartialAuthorityPointers {
  if (!isRecord(authority.appSpec)) {
    rejectAppSpecStructure("partial root must be an object");
  }
  assertClosedApplicationSpecSurface(authority.appSpec);
  if (!authority.appSpecRef.trim()) {
    throw new Error(
      "Partial application authority requires a nonempty AppSpec reference.",
    );
  }
  if (
    authority.evidenceRefs.length === 0 ||
    authority.evidenceRefs.some((ref) => !ref.trim())
  ) {
    throw new Error(
      "Partial application authority requires nonempty evidence references.",
    );
  }
  const lockedPaths = parseUniqueApplicationAuthorityPointers(
    authority.lockedPaths,
    "locked",
    APPLICATION_AUTHORITY_POINTER_LIMITS.maxLockedPaths,
  );
  const openSlots = parseUniqueApplicationAuthorityPointers(
    authority.openSlots,
    "open-slot",
    APPLICATION_AUTHORITY_POINTER_LIMITS.maxOpenSlots,
  );
  rejectOverlappingPointers(lockedPaths, "locked");
  rejectOverlappingPointers(openSlots, "open-slot");
  rejectLockedOpenOverlap(lockedPaths, openSlots);
  for (const pointer of lockedPaths) {
    readApplicationAuthorityPointer(
      authority.appSpec,
      pointer,
      "Human application authority",
    );
  }
  return { lockedPaths, openSlots };
}

/**
 * Candidate-independent admission for partial human authority. Call this
 * before invoking a semantic planner; candidate-dependent completeness and
 * open-slot reads remain enforced by the merge boundary below.
 */
export function validatePartialProvidedApplicationAuthority(
  authority: PartialProvidedApplicationAuthority,
): void {
  validatePartialAuthorityFacts(authority);
}

export function mergePlannerCandidateIntoOpenSlots(input: {
  authority: PartialProvidedApplicationAuthority;
  candidate: AppSpecFull;
}): AppSpecFull {
  const { openSlots } = validatePartialAuthorityFacts(input.authority);

  const merged = structuredClone(input.authority.appSpec) as Record<
    string,
    unknown
  >;
  for (const pointer of openSlots) {
    const candidateValue = readApplicationAuthorityPointer(
      input.candidate,
      pointer,
      "Planner candidate",
    );
    if (candidateValue === undefined) {
      throw new Error(
        `Planner candidate does not contain a value at declared path ${pointer.canonical}.`,
      );
    }
    writeOwnPointer(merged, pointer, candidateValue, input.candidate);
  }

  assertFullApplicationSpec(merged);
  assertResponsibilityCoverage(merged);
  return merged;
}

export function promptAppPlanFromProvidedAppSpec(
  appSpec: AppSpecFull,
): PromptAppPlan & { appSpecRef: string } {
  assertFullApplicationSpec(appSpec);
  assertResponsibilityCoverage(appSpec);
  const clonedAppSpec = structuredClone(appSpec);
  return {
    appSpec: clonedAppSpec,
    appSpecRef: fingerprintAppGenerationSpec(appSpec),
    pages: structuredClone(clonedAppSpec.pages ?? []),
    blocks: clonedAppSpec.blocks.map((block) => ({
      ...structuredClone(block),
      binds: structuredClone(block.binds ?? []),
    })),
    ...(clonedAppSpec.themeHint
      ? { themeHint: structuredClone(clonedAppSpec.themeHint) }
      : {}),
    ...(clonedAppSpec.designSystemTokens
      ? {
          designSystemTokens: structuredClone(clonedAppSpec.designSystemTokens),
        }
      : {}),
    ...(clonedAppSpec.designSystemContract
      ? {
          designSystemContract: structuredClone(
            clonedAppSpec.designSystemContract,
          ),
        }
      : {}),
  };
}
